import {Injectable} from '@angular/core';
import {PageItem} from '@models/sidebar/page-item';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {TemplateMenuItem} from '@models/sidebar/response/template-menu-item';
import {UserDefinedWidget} from '@models/pages/user-defined-page/user-defined-widget';
import {GlobalHelperService} from '@services/global-helper/global-helper.service';
import {TemplateStatusPageSignalDetails} from '@models/sidebar/request/template-page-configuration-details';
import {StatusEventPage} from '@models/sidebar/response/status-event-page';
import {TemplatePageConfiguration} from '@models/sidebar/request/template-page-configuration';
import {StatusEventSignalEventsDetails} from '@models/machine/status-event-signal/status-event-signal-events-details';


@Injectable({
  providedIn: 'root'
})
export class TemplatePagesService {

  private CREATE_TEMPLATE_PAGE_URL_SUFFIX: string = '/api/web/base/:machineId/userMenu/templatePages?name=:name&status=:isStatusPage';
  private DELETE_TEMPLATE_PAGE_URL_SUFFIX: string = '/api/web/base/:machineId/userMenu/templatePages/:pageId';
  private GET_TEMPLATE_PAGE_URL_SUFFIX: string = '/api/web/base/:machineId/userMenu/templatePages/:pageId/:isStatusPage';
  private NAME_ALREADY_USED_TEMPLATE_PAGE_URL_SUFFIX: string = '/api/web/base/:machineId/userMenu/templatePages/nameAlreadyUsed?name=:name';
  private IS_FUNCTIONALITY_AVAILABLE_CREATE_TEMPLATE_PAGE_URL_SUFFIX: string = '/api/web/base/:machineId/userMenu/templatePages/isAvailable';
  private RENAME_TEMPLATE_PAGE_URL_SUFFIX: string = '/api/web/base/:machineId/userMenu/templatePages/:pageId/rename?name=:name';
  private SAVE_TEMPLATE_USER_DEFINED_PAGE_WIDGETS_URL_SUFFIX: string = '/api/web/base/:machineId/userMenu/templatePages/:pageId/saveWidgets';
  private SAVE_TEMPLATE_STATUS_EVENT_SIGNALS_URL_SUFFIX: string = '/api/web/base/:machineId/userMenu/templatePages/:pageId/saveStatusEventSignals';
  private SYNC_TEMPLATE_PAGE_URL_SUFFIX: string = '/api/web/base/:machineId/userMenu/templatePages/:pageId/synchronize?synchronize=:synchronize';
  private savingIndicatorMessage = '';
  private errorCreatingPage = '';
  private successCreatingPage = '';
  private errorRenamingPage = '';
  private successRenamingPage = '';

  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService,
              private loadingIndicator: LoadingIndicatorService,
              private notificationService: NotificationService,
              private menuNotificationService: MenuNotificationService,
              private translateService: TranslateService,
              private globalHelper: GlobalHelperService,
  ) {
    this.BASE_URL = this.apiLocation.getLocation();
    this.translatePageMessages();
  }

  public create(templateMenuItems: PageItem[], baseMenuItems: PageItem[], pageItem: PageItem, machineId: string | null, currentPageName: string, parentId: string | null, isAdminOrHigher: boolean, isParentMachine: boolean, firebaseUserId: string, isStatusPage: boolean): void {
    if (machineId && parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      this.createTemplatePage(machineId, pageItem.name, isStatusPage).subscribe((newTemplateMenuItem: TemplateMenuItem) => {
        this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, pageItem.name, ''));
        this.setNewTemplatePageProperties(pageItem, newTemplateMenuItem, machineId, firebaseUserId);
        templateMenuItems.push(pageItem);
        baseMenuItems.push(pageItem);
        this.loadingIndicator.hide();
        this.notificationService.success(this.successCreatingPage, currentPageName, false);
      }, (error) => {
        this.notificationService.error(this.errorCreatingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  public delete(machineId: string, pageId: string) {
    let url: string = this.BASE_URL + this.DELETE_TEMPLATE_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId))
    .replace(':pageId', pageId);
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public getPageDetails(machineId: string, pageId: string, isStatusPage: boolean) {
    let url: string = this.BASE_URL + this.GET_TEMPLATE_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId))
    .replace(':pageId', String(pageId))
    .replace(':isStatusPage', String(isStatusPage));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public isTemplateFunctionalityAvailable(machineId: string) {
    let url: string = this.BASE_URL + this.IS_FUNCTIONALITY_AVAILABLE_CREATE_TEMPLATE_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public isPageNameAlreadyUsed(machineId: string, pageName: string) {
    let url: string = this.BASE_URL + this.NAME_ALREADY_USED_TEMPLATE_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':name', pageName);
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public rename(pageItem: PageItem, machineId: string | null, currentPageName) {
    if (machineId && pageItem.id && pageItem.pageId) {
      const pageId: string = pageItem.pageId;
      this.loadingIndicator.show(this.savingIndicatorMessage);
      this.changePageName(machineId, pageId, pageItem.name).subscribe(() => {
        this.globalHelper.handleUpdatedPage(pageItem.name, '', currentPageName, pageId, this.successRenamingPage);
        this.loadingIndicator.hide();
      }, (error) => {
        this.notificationService.error(this.errorRenamingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  public saveWidgets(machineId: number, pageId: number, widgets: UserDefinedWidget[]): Observable<any> {
    let url: string = this.BASE_URL + this.SAVE_TEMPLATE_USER_DEFINED_PAGE_WIDGETS_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId))
    .replace(':pageId', String(pageId));
    return this.http.post(url, widgets, HTTPS_REQUEST_OPTIONS);
  }

  public saveStatusEventSignals(machineId: string, pageId: string, widgets: TemplateStatusPageSignalDetails[]): Observable<any> {
    let url: string = this.BASE_URL + this.SAVE_TEMPLATE_STATUS_EVENT_SIGNALS_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId))
    .replace(':pageId', String(pageId));
    return this.http.post(url, widgets, HTTPS_REQUEST_OPTIONS);
  }

  public setSynchronizationFlag(machineId: number | string, pageId: string, syncFlag: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.SYNC_TEMPLATE_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId))
    .replace(':pageId', String(pageId))
    .replace(':synchronize', String(syncFlag));
    return this.http.post(url, {}, HTTPS_REQUEST_OPTIONS);
  }

  public parseTemplatePageToStatusPage(page: StatusEventPage | TemplatePageConfiguration, machineId: string, isTemplatePage: boolean): [StatusEventPage, boolean] {
    let syncTemplatePage: boolean = false;
    if (!page) {
      return [new StatusEventPage(-1, ''), syncTemplatePage];
    }

    let statusEventPage: StatusEventPage = page as StatusEventPage;
    if (isTemplatePage || !statusEventPage) {
      const templatePage = page as TemplatePageConfiguration;
      statusEventPage = new StatusEventPage(page.id, page.name);
      statusEventPage.machineId = Number(machineId);
      statusEventPage.menuItemId = String(templatePage.id);
      for (const [key, value] of Object.entries(templatePage?.configurationDetails?.statusSignalsDetails)) {
        const { id, name, label, unit, parsingType, definitions, machineId } = value;
        statusEventPage.statusEventsDetails.set(Number(key), new StatusEventSignalEventsDetails(
          id, name, label, unit, parsingType, new Map(Object.entries(definitions)), machineId
        ));
      }
      statusEventPage.signals = [...templatePage?.configurationDetails?.statusSignals.map(s => s.signalId)];
      syncTemplatePage = templatePage.synchronizationEnabled;
    }
    return [statusEventPage, syncTemplatePage];
  }

  private changePageName(machineId: string, pageId: string, pageName: string): Observable<any> {
    let url: string = this.BASE_URL + this.RENAME_TEMPLATE_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId))
    .replace(':pageId', pageId)
    .replace(':name', pageName);
    return this.http.post(url, {}, HTTPS_REQUEST_OPTIONS);
  }

  private setNewTemplatePageProperties(pageItem: PageItem, templateMenuItem: TemplateMenuItem, machineId: string, firebaseUserId: string): void {
    pageItem.machineId = machineId;
    pageItem.firebaseUserId = firebaseUserId;
    pageItem.pageId = templateMenuItem.pageId;
    pageItem.id = templateMenuItem.pageId;
    pageItem.canBeAltered = true;
    pageItem.parentId = machineId;
  }

  private createTemplatePage(machineId: string, pageName: string, isStatusPage: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.CREATE_TEMPLATE_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':name', pageName)
    .replace(':isStatusPage', String(isStatusPage));
    return this.http.post<TemplateMenuItem>(url, {}, HTTPS_REQUEST_OPTIONS);
  }

  private translatePageMessages() {
    this.savingIndicatorMessage = this.translateService.instant('Saving');
    this.errorCreatingPage = this.translateService.instant('Error creating user defined page.');
    this.successCreatingPage = this.translateService.instant('User defined page was created.');
    this.errorRenamingPage = this.translateService.instant('Error renaming user defined page.');
    this.successRenamingPage = this.translateService.instant('User defined page was renamed.');
  }
}
